import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Sparkles, Download, CheckCircle2, CalendarPlus, Plus, X, Loader2, FileText, ChevronDown, Clock3 } from "lucide-react";
import { API_BASE } from "../lib/api";
import axios from "axios";
import RenderMarkdown from "../components/RenderMarkdown";
import useForceLightTheme from "../lib/useForceLightTheme";

// Human-readable labels for `installation_type` values. Kept small + local
// (rather than importing jobTypes.js) so the public bundle stays lean.
const INSTALL_LABELS = {
  wrap_colour_change: "Vinyl Wrap — Full Colour Change",
  wrap_business: "Vinyl Wrap — Print / Branding",
  wrap_interior: "Vinyl Wrap — Interior",
  wrap_partial: "Vinyl Wrap — Partial",
  wrap_chrome_delete: "Chrome Delete",
  ppf_partial: "PPF — Partial",
  ppf_full_front: "PPF — Front Coverage",
  ppf_full_body: "PPF — Full Body",
  ppf_colour: "Colour PPF — Full Body",
  ppf_colour_partial: "Colour PPF — Partial",
  tinting: "Window Tinting",
  ceramic: "Ceramic Coating",
  detail_full: "Detail — Full Vehicle",
  detail_exterior: "Detail — Exterior",
  detail_interior: "Detail — Interior",
  detail_paint_correction: "Detail — Paint Correction",
  detail_engine_bay: "Detail — Engine Bay",
  detail_headlight_restoration: "Detail — Headlight Restoration",
  signage_decals: "Signage / Decals",
  body_kit_install: "Body kit installation",
  headlight_tint: "Headlight lens tint",
  taillight_tint: "Taillight lens tint",
  lens_tint: "Headlight / Taillight Tint",
  lens_tint_full_set: "Lens tint — full set",
  badge_modification: "Badge Modification",
  print_only: "Print only",
  plot_only: "Plot / cut only",
  print_and_plot: "Print + plot",
  large_format_print: "Large-format print",
  vehicle_decals: "Vehicle decals / lettering",
  other: "Other",
};
const prettyInstall = (v) => INSTALL_LABELS[v] || v || "Installation";

const LENS_AREA_LABELS = {
  full_vehicle: "Full vehicle tint",
  headlight: "Headlight",
  taillight: "Taillight",
  taillight_reflectors: "Taillight + Reflectors",
  reflectors: "Reflectors",
};
const prettyLensArea = (v) => LENS_AREA_LABELS[v] || v || "";

const BADGE_SPEC_LABELS = {
  badge_delete: "Badge delete",
  badge_blackout: "Badge blackout",
  badge_replace: "Badge replace",
  custom_badge: "Custom badge",
  sticker_replacement: "Sticker replacement",
};
const prettyBadgeSpec = (v) => BADGE_SPEC_LABELS[v] || v || "";

/**
 * Public, unauthenticated quote view.
 * Customers can:
 *  - See the quote summary + download PDF
 *  - Accept the quote by typing their name (electronic signature)
 *  - Suggest up to 3 preferred install dates
 *  - Leave an optional message for the shop
 */

const todayIso = () => {
  const d = new Date();
  // yyyy-mm-dd in local tz so the min="…" attribute on the date input
  // matches what the customer sees on their calendar.
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const prettyDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function PublicQuotePage() {
  useForceLightTheme();
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");

  const [showAcceptForm, setShowAcceptForm] = useState(false);
  const [signature, setSignature] = useState("");
  const [dates, setDates] = useState([""]);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    axios.get(`${API_BASE}/public/quote/${token}`)
      .then(({ data }) => {
        setQuote(data);
        setSignature(data.contact_name || "");
      })
      .catch(() => setError("Quote not found"));
  }, [token]);

  const min = useMemo(() => todayIso(), []);
  const alreadyAccepted = !!quote?.customer_accepted_at;
  const choiceMode = quote?.customer_choice === "any" || quote?.customer_choice === "one";
  const choosing = choiceMode && !alreadyAccepted;
  const toggleOption = (key) => {
    if (!choosing) return;
    if (quote.customer_choice === "one") setSelected([key]);
    else setSelected((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
  };
  const allInstalls = (quote?.job_items || []).flatMap((it) => it.installations || []);
  const selectionTotal = choosing
    ? allInstalls.filter((i) => selected.includes(i.key)).reduce((n, i) => n + Number(i.price || 0), 0)
    : Number(quote?.amount || 0);

  const addDate = () => {
    if (dates.length >= 3) return;
    setDates([...dates, ""]);
  };
  const updateDate = (i, v) => {
    setDates(dates.map((d, idx) => (idx === i ? v : d)));
  };
  const removeDate = (i) => {
    setDates(dates.filter((_, idx) => idx !== i));
  };

  const submitAccept = async () => {
    setFormError("");
    if (!signature.trim()) {
      setFormError("Please type your full name to accept");
      return;
    }
    if (choosing && selected.length === 0) {
      setFormError("Please choose at least one option above");
      return;
    }
    setSubmitting(true);
    try {
      const cleanDates = dates.map((d) => d.trim()).filter(Boolean);
      const { data } = await axios.post(`${API_BASE}/public/quote/${token}/accept`, {
        signature_name: signature.trim(),
        preferred_dates: cleanDates,
        message: message.trim(),
        selected_installations: choosing ? selected : [],
      });
      if (choosing) {
        const fresh = await axios.get(`${API_BASE}/public/quote/${token}`);
        setQuote(fresh.data);
        setShowAcceptForm(false);
        return;
      }
      setQuote((q) => ({
        ...q,
        customer_accepted_at: data.customer_accepted_at,
        customer_signature_name: data.customer_signature_name,
        customer_preferred_dates: data.customer_preferred_dates,
        customer_message: data.customer_message,
        sub_status: "accepted",
      }));
      setShowAcceptForm(false);
    } catch (e) {
      setFormError(e?.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="wc-public-light min-h-screen flex items-center justify-center px-4 bg-slate-50">
        <div className="glass rounded-3xl p-8 text-slate-700" data-testid="public-quote-error">{error}</div>
      </div>
    );
  }
  if (!quote) {
    return (
      <div className="wc-public-light min-h-screen flex items-center justify-center px-4 bg-slate-50">
        <div className="glass rounded-3xl p-8 text-slate-700">Loading…</div>
      </div>
    );
  }

  const pdfUrl = `${API_BASE}/public/quote/${token}/pdf`;
  const bizName = quote.business?.name || "WrapConsole";
  const accent = (quote.business?.primary_color || "").trim() || null;

  return (
    <div
      className="wc-public-light min-h-screen grain px-4 py-16 flex justify-center bg-slate-50 text-slate-900"
      style={accent ? { "--wc-accent": accent } : undefined}
    >
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-2 mb-8 justify-center">
          {quote.business?.logo_url ? (
            <img
              src={quote.business.logo_url}
              alt={bizName}
              className="max-h-10 max-w-[180px]"
              data-testid="public-quote-logo"
            />
          ) : (
            <>
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-500/40 to-emerald-500/30 border border-white/10 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading text-xl text-white">{bizName}</span>
            </>
          )}
        </div>

        <div className="glass-strong rounded-3xl p-8 md:p-10 space-y-6">
          <div>
            <div className="text-label">Quote</div>
            <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tighter text-white mt-1">
              {choosing ? (
                <span data-testid="public-quote-headline-options">Priced per option</span>
              ) : quote.is_estimate && Number(quote.amount_max) > Number(quote.amount) ? (
                <>${Number(quote.amount).toLocaleString()} – ${Number(quote.amount_max).toLocaleString()}</>
              ) : (
                <>${Number(quote.amount).toLocaleString()}</>
              )}
            </h1>
            <p className="text-slate-400 mt-2">
              Prepared for {quote.contact_name}
              {quote.is_estimate && <span className="ml-2 inline-flex items-center gap-1 pill pill-warning text-[10px] uppercase tracking-wider">Estimate — final price may vary</span>}
            </p>
          </div>

          {/* Low-confidence AI estimate — nudges the customer to book a
              shop visit for a firm quote instead of relying on this rough
              range. Only shown when the AI scored 1 or 2 out of 5. */}
          {Number(quote.auto_quote_confidence) > 0 && Number(quote.auto_quote_confidence) <= 2 && (
            <div
              data-testid="public-quote-low-confidence"
              className="bg-sky-50 border border-sky-300 rounded-2xl p-4 flex gap-3 items-start"
            >
              <div className="w-9 h-9 rounded-full bg-sky-100 border border-sky-300 flex items-center justify-center shrink-0">
                <span className="text-sky-700 text-xs font-bold">i</span>
              </div>
              <div className="text-sm">
                <div className="text-sky-900 font-semibold">This is a rough estimate</div>
                <div className="text-slate-300 mt-1">
                  Your vehicle has details that make it hard to price accurately from a description alone — a body kit, unusual bodywork, or a non-standard model. Book a quick shop visit for a firm quote.
                </div>
                {quote.business?.contact_phone && (
                  <div className="text-slate-400 text-xs mt-2">Call {quote.business.contact_phone} to book a fitting.</div>
                )}
              </div>
            </div>
          )}

          {/* Vehicle grid — collapses when the quote has multi-vehicle
              job_items (rendered as per-vehicle cards below). */}
          {(!quote.job_items || quote.job_items.length <= 1) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4">
                <div className="text-label">Vehicle</div>
                <div className="text-white mt-1">{quote.vehicle}</div>
              </div>
              <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4">
                <div className="text-label">Service</div>
                <div className="text-white mt-1">{quote.service}</div>
              </div>
            </div>
          )}

          {/* Install-time marker — customer-facing duration the shop has
              committed to. Renders as a standalone card so it reads as a
              commitment, not a line-item afterthought. Skipped silently
              when the office hasn't filled it in. */}
          {(quote.customer_install_duration || "").trim() && (
            <div
              data-testid="public-quote-install-time"
              className="bg-emerald-500/[0.06] border border-emerald-400/25 rounded-2xl p-4 flex items-center gap-3"
            >
              <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
                <Clock3 className="w-5 h-5 text-emerald-300" />
              </div>
              <div className="min-w-0">
                <div className="text-label text-emerald-300">Install time</div>
                <div className="text-white font-medium mt-0.5">{quote.customer_install_duration}</div>
              </div>
            </div>
          )}

          {/* Per-vehicle quote breakdown — one card per vehicle with its
              own installation list and subtotal. Shown whenever the quote
              has a `job_items` array (new multi-vehicle structure). Items
              flagged `quote_separately` get a distinct "Separate option"
              treatment so customers comparing alternatives can see each
              price as a standalone block instead of a roll-up. */}
          {Array.isArray(quote.job_items) && quote.job_items.length > 0 ? (
            <div data-testid="public-quote-job-items" className="space-y-4">
              {choosing && (
                <div data-testid="public-quote-choice-hint" className="rounded-2xl border border-sky-300 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                  {quote.customer_choice === "one"
                    ? "Each installation is priced separately — choose the one option you'd like."
                    : "Each installation is priced separately — tick the ones you'd like and your total updates below."}
                </div>
              )}
              {(() => {
                // Pre-compute which items are separate options so we can
                // number them "Option 1 / Option 2" independently from
                // the "Vehicle 1 of N" numbering used for roll-up items.
                const items = quote.job_items;
                let separateCounter = 0;
                let rollupCounter = 0;
                const rollupTotalItems = items.filter((it) => !it.quote_separately).length;
                return items.map((item, vi) => {
                const hasPrices = (item.installations || []).some((i) => Number(i.price) > 0);
                const isSeparate = !!item.quote_separately;
                let labelPrefix;
                if (isSeparate) {
                  separateCounter += 1;
                  labelPrefix = `Option ${separateCounter}`;
                } else {
                  rollupCounter += 1;
                  labelPrefix = rollupTotalItems > 1
                    ? `Vehicle ${rollupCounter} of ${rollupTotalItems}`
                    : "Vehicle";
                }
                return (
                  <div
                    key={item.id || vi}
                    data-testid={`public-quote-vehicle-${vi}`}
                    data-separate={isSeparate ? "true" : "false"}
                    className={
                      isSeparate
                        ? "bg-sky-50 border-2 border-sky-500 rounded-2xl p-4 md:p-5 space-y-4 shadow-[0_0_0_1px_rgba(14,165,233,0.15)]"
                        : "bg-white/[0.03] border border-white/10 rounded-2xl p-4 md:p-5 space-y-4"
                    }
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className={isSeparate ? "text-[10px] uppercase tracking-widest text-sky-800 font-semibold" : "text-label"}>
                          {labelPrefix}
                          {isSeparate && (
                            <span className="ml-2 inline-block px-1.5 py-0.5 rounded-full bg-sky-600 text-white text-[9px] normal-case tracking-normal font-semibold">
                              separate option
                            </span>
                          )}
                        </div>
                        <div className={isSeparate ? "text-slate-900 text-lg font-semibold mt-0.5" : "text-white text-lg font-medium mt-0.5"}>{item.vehicle}</div>
                      </div>
                      {hasPrices && !choosing && (
                        <div className="text-right">
                          <div className={isSeparate ? "text-[10px] uppercase tracking-widest text-sky-800" : "text-label"}>
                            {isSeparate ? "Option price" : "Vehicle subtotal"}
                          </div>
                          <div
                            data-testid={`public-quote-vehicle-${vi}-subtotal`}
                            className={isSeparate ? "text-sky-800 text-xl font-bold mt-0.5" : "text-white text-lg font-semibold mt-0.5"}
                          >
                            ${Number(item.subtotal || 0).toLocaleString()}
                          </div>
                        </div>
                      )}
                    </div>

                    {(item.installations || []).length > 0 && (
                      <ul className="space-y-3">
                        {item.installations.map((inst, i) => {
                          const areas = [...(inst.wrap_areas || []), ...(inst.ppf_areas || []), ...(inst.tint_areas || [])];
                          if (inst.lens_area) areas.push(prettyLensArea(inst.lens_area));
                          (inst.badge_specifics || []).forEach((b) => areas.push(prettyBadgeSpec(b)));
                          const picked = selected.includes(inst.key);
                          return (
                            <li
                              key={i}
                              data-testid={`public-quote-vehicle-${vi}-install-${i}`}
                              data-selected={picked ? "true" : "false"}
                              onClick={choosing ? () => toggleOption(inst.key) : undefined}
                              className={choosing
                                ? `rounded-xl border-2 p-3 cursor-pointer transition-colors ${picked ? "border-emerald-500 bg-emerald-50" : "border-white/15 bg-white/[0.03] hover:border-white/40"}`
                                : "border-l-2 border-white/10 pl-3"}
                            >
                              <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="text-white text-sm font-medium flex items-center gap-2.5">
                                  {choosing && (
                                    <span
                                      data-testid={`public-quote-option-${inst.key}`}
                                      className={`w-5 h-5 shrink-0 border-2 flex items-center justify-center ${quote.customer_choice === "one" ? "rounded-full" : "rounded-[4px]"} ${picked ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-400"}`}
                                    >
                                      {picked && <CheckCircle2 className="w-3.5 h-3.5" />}
                                    </span>
                                  )}
                                  {prettyInstall(inst.installation_type)}
                                </div>
                                {Number(inst.price) > 0 && (
                                  <div
                                    data-testid={`public-quote-vehicle-${vi}-install-${i}-price`}
                                    className="text-white text-sm font-semibold tabular-nums"
                                  >
                                    ${Number(inst.price).toLocaleString()}
                                  </div>
                                )}
                              </div>
                              {areas.length > 0 && (
                                <div className="text-sm text-slate-300 mt-1.5 flex gap-2">
                                  <span className="text-xs uppercase tracking-widest text-slate-500 shrink-0 pt-0.5 min-w-[70px]">Areas</span>
                                  <span>{areas.join(", ")}</span>
                                </div>
                              )}
                              {inst.material && (
                                <div className="text-sm text-slate-300 mt-1 flex gap-2">
                                  <span className="text-xs uppercase tracking-widest text-slate-500 shrink-0 pt-0.5 min-w-[70px]">Material</span>
                                  <span>{inst.material}</span>
                                </div>
                              )}
                              {inst.notes && (
                                <div className="text-sm text-slate-300 mt-1 flex gap-2">
                                  <span className="text-xs uppercase tracking-widest text-slate-500 shrink-0 pt-0.5 min-w-[70px]">Notes</span>
                                  <span className="whitespace-pre-wrap">{inst.notes}</span>
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              });
              })()}

              {/* Total line — only shown when at least one installation has a price. */}
              {quote.job_items.some((it) => (it.installations || []).some((i) => Number(i.price) > 0)) && (
                <div
                  data-testid="public-quote-total"
                  className="flex items-center justify-between bg-white/[0.05] border border-white/15 rounded-2xl px-5 py-4"
                >
                  <div>
                    <div className="text-label">{choiceMode ? "Your selection" : "Total"}</div>
                    {choosing && (
                      <div className="text-[11px] text-slate-500 mt-0.5" data-testid="public-quote-selection-count">
                        {selected.length === 0 ? "Nothing chosen yet" : `${selected.length} option${selected.length === 1 ? "" : "s"} chosen`}
                      </div>
                    )}
                  </div>
                  <div className="text-white text-2xl font-semibold tabular-nums" data-testid="public-quote-total-amount">
                    ${selectionTotal.toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          ) : (
            quote.installations && quote.installations.length > 0 && (
              <div data-testid="public-quote-installations" className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 space-y-3">
                <div className="text-label">Installation details</div>
                <ul className="space-y-3">
                  {quote.installations.map((inst, i) => {
                    const areas = [...(inst.wrap_areas || []), ...(inst.ppf_areas || []), ...(inst.tint_areas || [])];
                    if (inst.lens_area) areas.push(prettyLensArea(inst.lens_area));
                    (inst.badge_specifics || []).forEach((b) => areas.push(prettyBadgeSpec(b)));
                    return (
                      <li
                        key={i}
                        data-testid={`public-quote-install-${i}`}
                        className="border-l-2 border-white/10 pl-3"
                      >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="text-white text-sm font-medium">
                            {prettyInstall(inst.installation_type)}
                          </div>
                          {Number(inst.price) > 0 && (
                            <div className="text-white text-sm font-semibold tabular-nums">
                              ${Number(inst.price).toLocaleString()}
                            </div>
                          )}
                        </div>
                        {areas.length > 0 && (
                          <div
                            data-testid={`public-quote-install-${i}-areas`}
                            className="text-sm text-slate-300 mt-1.5 flex gap-2"
                          >
                            <span className="text-xs uppercase tracking-widest text-slate-500 shrink-0 pt-0.5 min-w-[70px]">Areas</span>
                            <span>{areas.join(", ")}</span>
                          </div>
                        )}
                        {inst.material && (
                          <div
                            data-testid={`public-quote-install-${i}-material`}
                            className="text-sm text-slate-300 mt-1 flex gap-2"
                          >
                            <span className="text-xs uppercase tracking-widest text-slate-500 shrink-0 pt-0.5 min-w-[70px]">Material</span>
                            <span>{inst.material}</span>
                          </div>
                        )}
                        {inst.notes && (
                          <div
                            data-testid={`public-quote-install-${i}-notes`}
                            className="text-sm text-slate-300 mt-1 flex gap-2"
                          >
                            <span className="text-xs uppercase tracking-widest text-slate-500 shrink-0 pt-0.5 min-w-[70px]">Notes</span>
                            <span className="whitespace-pre-wrap">{inst.notes}</span>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )
          )}

          {alreadyAccepted && (quote.declined_installations || []).length > 0 && (
            <div data-testid="public-quote-declined" className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm">
              <div className="text-label mb-1.5">Not selected</div>
              {quote.declined_installations.map((d, i) => (
                <div key={i} className="flex justify-between text-slate-500 line-through">
                  <span>{prettyInstall(d.installation_type)}{d.vehicle ? ` — ${d.vehicle}` : ""}</span>
                  {d.price > 0 && <span>${Number(d.price).toLocaleString()}</span>}
                </div>
              ))}
            </div>
          )}

          {quote.terms && (
            <TermsReview terms={quote.terms} />
          )}

          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="public-quote-pdf"
            className="w-full flex items-center justify-center gap-2 bg-white/10 border border-white/10 text-white font-medium rounded-full py-3 hover:bg-white/15 transition"
          >
            <Download className="w-4 h-4" /> Download PDF
          </a>

          {/* ---------------- Acceptance state ---------------- */}
          {alreadyAccepted ? (
            <AcceptedView quote={quote} />
          ) : showAcceptForm ? (
            <AcceptForm
              signature={signature}
              setSignature={setSignature}
              dates={dates}
              addDate={addDate}
              updateDate={updateDate}
              removeDate={removeDate}
              min={min}
              message={message}
              setMessage={setMessage}
              submitting={submitting}
              submitAccept={submitAccept}
              formError={formError}
              onCancel={() => setShowAcceptForm(false)}
            />
          ) : (
            <button
              onClick={() => setShowAcceptForm(true)}
              disabled={choosing && selected.length === 0}
              data-testid="public-quote-accept-btn"
              className="w-full flex items-center justify-center gap-2 font-semibold rounded-full py-3.5 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: "var(--wc-accent, #10b981)",
                color: "#fff",
                boxShadow: "0 10px 25px -5px rgba(16,185,129,0.3)",
              }}
            >
              <CheckCircle2 className="w-5 h-5" />
              {choosing
                ? (selected.length === 0 ? "Choose an option above to accept" : `Accept selection ($${selectionTotal.toLocaleString()}) & pick dates`)
                : <>Accept quote &amp; pick dates</>}
            </button>
          )}

          <p className="text-xs text-slate-500 text-center">
            Valid for 30 days from issue. Final pricing may vary based on vehicle inspection.
          </p>
        </div>

        <div className="text-center text-xs text-slate-600 mt-6">
          Powered by <span className="text-slate-400">{bizName}</span>
        </div>
      </div>
    </div>
  );
}

function AcceptForm({
  signature, setSignature,
  dates, addDate, updateDate, removeDate, min,
  message, setMessage,
  submitting, submitAccept, formError, onCancel,
}) {
  return (
    <div
      data-testid="public-quote-accept-form"
      className="bg-emerald-500/[0.06] border border-emerald-500/30 rounded-2xl p-5 space-y-4"
    >
      <div>
        <div className="text-label text-emerald-300 mb-1">Accept this quote</div>
        <div className="text-slate-300 text-sm">
          Type your full name to accept, then pick up to three preferred install dates. We'll be in touch to confirm.
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-slate-400 block mb-1.5">Your full name (electronic signature)</label>
        <input
          type="text"
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          data-testid="public-quote-signature"
          placeholder="e.g. Alex Morgan"
          className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 focus:outline-none focus:border-emerald-400/50"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs uppercase tracking-widest text-slate-400">Preferred dates (up to 3)</label>
          {dates.length < 3 && (
            <button
              type="button"
              onClick={addDate}
              data-testid="public-quote-add-date"
              className="text-xs text-emerald-300 hover:text-emerald-200 flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add date
            </button>
          )}
        </div>
        <div className="space-y-2">
          {dates.map((d, i) => (
            <div key={i} className="flex items-center gap-2">
              <CalendarPlus className="w-4 h-4 text-slate-500 shrink-0" />
              <input
                type="date"
                value={d}
                min={min}
                onChange={(e) => updateDate(i, e.target.value)}
                data-testid={`public-quote-date-${i}`}
                className="flex-1 bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 text-white focus:outline-none focus:border-emerald-400/50"
              />
              {dates.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeDate(i)}
                  data-testid={`public-quote-remove-date-${i}`}
                  className="p-1.5 text-slate-500 hover:text-white"
                  aria-label="Remove date"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-slate-400 block mb-1.5">Message (optional)</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid="public-quote-message"
          rows={3}
          placeholder="Anything the team should know?"
          className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-slate-600 focus:outline-none focus:border-emerald-400/50 resize-none"
        />
      </div>

      {formError && (
        <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2" data-testid="public-quote-form-error">
          {formError}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="flex-1 rounded-full py-2.5 bg-white/[0.04] border border-white/10 text-slate-300 hover:bg-white/[0.08] transition text-sm"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submitAccept}
          disabled={submitting}
          data-testid="public-quote-submit-accept"
          className="flex-[2] rounded-full py-2.5 bg-emerald-500 text-black font-semibold hover:bg-emerald-400 transition disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <><CheckCircle2 className="w-4 h-4" /> Accept quote</>}
        </button>
      </div>
    </div>
  );
}

function AcceptedView({ quote }) {
  const dates = quote.customer_preferred_dates || [];
  return (
    <div
      data-testid="public-quote-accepted-view"
      className="bg-emerald-500/[0.08] border border-emerald-500/30 rounded-2xl p-6 text-center space-y-3"
    >
      <div className="mx-auto w-12 h-12 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
        <CheckCircle2 className="w-6 h-6 text-emerald-300" />
      </div>
      <div>
        <div className="text-white font-semibold text-lg">Thanks, {quote.customer_signature_name || quote.contact_name}!</div>
        <div className="text-slate-300 text-sm mt-1">
          Your quote is accepted. We'll be in touch shortly to confirm a booking.
        </div>
      </div>

      {dates.length > 0 && (
        <div className="text-left bg-white/[0.03] border border-white/10 rounded-xl p-3">
          <div className="text-label mb-1">Your preferred dates</div>
          <ul className="text-slate-200 text-sm space-y-0.5">
            {dates.map((d) => (
              <li key={d} data-testid="public-quote-accepted-date">{prettyDate(d)}</li>
            ))}
          </ul>
        </div>
      )}

      {quote.business?.contact_phone && (
        <div className="text-xs text-slate-500">
          Questions? Call {quote.business.contact_phone}
        </div>
      )}
    </div>
  );
}

/**
 * Collapsible "Review Terms & Conditions" panel. Renders the shop's
 * installation-terms markdown as plain text (line-preserving) so the
 * customer can read them before formally signing later on the dedicated
 * `/public/terms/{token}` page once the job is scheduled.
 */
function TermsReview({ terms }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid="public-quote-terms"
      className="bg-white/[0.03] border border-white/10 rounded-2xl"
    >
      <button
        type="button"
        data-testid="public-quote-terms-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
      >
        <div className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
          <FileText className="w-4 h-4 text-slate-300" />
        </div>
        <div className="flex-1">
          <div className="text-white text-sm font-medium">
            Review {terms.title || "Terms & Conditions"}
          </div>
          <div className="text-xs text-slate-500">
            Read before accepting — you'll sign these when your job is scheduled.
          </div>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          data-testid="public-quote-terms-body"
          className="border-t border-white/10 px-4 py-3 max-h-72 overflow-y-scroll text-sm text-slate-300 leading-relaxed public-terms-scroll"
        >
          <RenderMarkdown source={terms.body_markdown} />
        </div>
      )}
    </div>
  );
}

